/* 
  This component displays a list of stale or failed recordings in a table format.
  It allows users to reprocess recordings and refresh the list.
*/
import {
  Box,
  MaterialTableWrapper,
  MaterialTableWrapperProps,
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_TableOptions,
  withThemeProvider,
  RawDriveInfo,
  RowActionButton,
  Icon,
  Button,
} from '@ouster/webviz-react-components';

import { useEffect, useMemo, useRef } from 'react';
import { CgDatabase } from 'react-icons/cg';

import DashboardCard from './DashboardCard';
import useChakraHook from '../../hooks/useChakraHook';
import { useStoreActions } from '../../reducers';
import { createPaginationConfig } from '../../utils/tableUtils';

const ThemedMaterialTableWrapper =
  withThemeProvider<MaterialTableWrapperProps<RawDriveInfo>>(
    MaterialTableWrapper
  );

type RecordingManagementStaleListProps = {
  data: RawDriveInfo[];
  osfStatus: 'NOT_STARTED' | 'FAILED';
  meta: { totalCount: number } | undefined;
  pagination: MRT_PaginationState;
  setPagination: (
    updater:
      | MRT_PaginationState
      | ((prevState: MRT_PaginationState) => MRT_PaginationState)
  ) => void;
  isLoading: boolean;
  isRefetching: boolean;
  refetch: () => void;
  reprocessedIds: Set<string>;
  setReprocessedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
};

export default function RecordingManagementStaleList({
  data,
  osfStatus,
  meta,
  pagination,
  setPagination,
  isLoading,
  isRefetching,
  refetch,
  reprocessedIds,
  setReprocessedIds,
}: RecordingManagementStaleListProps) {
  const { reprocessAnyRecording } = useStoreActions(
    (actions) => actions.adminDashboard
  );

  const { showToast } = useChakraHook();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  /* 
    Scroll to top when changing page
  */
  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [pagination.pageIndex]);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000); // 1 hour ago
    const idsToRemove = new Set<string>();

    data.forEach((row) => {
      const recordingId = `${row.organization_id}-${row.id}`;

      if (reprocessedIds.has(recordingId) && row.processed_at) {
        const processedAt = new Date(row.processed_at);

        // If processed_at is more recent than 1 hour ago, remove from reprocessedIds
        if (processedAt > oneHourAgo) {
          idsToRemove.add(recordingId);
        }
      }
    });

    // Remove the IDs if any were found
    if (idsToRemove.size > 0) {
      setReprocessedIds((prev) => {
        const newSet = new Set(prev);
        idsToRemove.forEach((id) => newSet.delete(id));
        return newSet;
      });
    }
  }, [data, reprocessedIds, setReprocessedIds]);

  const handleReprocessRecording = async (row: RawDriveInfo) => {
    try {
      const recordingId = `${row.organization_id}-${row.id}`;
      setReprocessedIds((prev) => new Set([...prev, recordingId]));
      await reprocessAnyRecording({
        drive_id: Number(row.id),
      });
    } catch (error) {
      // Remove from reprocessedIds if reprocess failed
      const recordingId = `${row.organization_id}-${row.id}`;
      setReprocessedIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(recordingId);
        return newSet;
      });

      showToast({
        title: 'Failed to reprocess recording',
        description: `${error}`,
        status: 'error',
      });
    }
  };

  const handleRefresh = () => {
    setReprocessedIds(new Set());
    refetch();
  };

  const columns = useMemo<MRT_ColumnDef<RawDriveInfo>[]>(
    () => [
      {
        accessorKey: 'organization_name',
        header: 'Organization',
        minSize: 250,
      },
      {
        accessorKey: 'id',
        header: 'Drive ID',
        minSize: 60,
      },
      {
        accessorKey: 'processed_at',
        header: 'Uploaded at',
        Cell: ({ row }) => {
          const processedAt = row.original.processed_at;
          return typeof processedAt === 'string'
            ? new Date(processedAt).toLocaleString()
            : 'Invalid Date';
        },
      },
      {
        accessorKey: 'uploaded_by',
        header: 'Uploader',
      },
      {
        accessorKey: 'recording_title',
        header: 'File Name',
      },
      {
        accessorKey: 'osf_generation_status',
        header: 'OSF Status',
      },
      {
        accessorKey: 'slam_generation_status',
        header: 'SLAM Status',
      },
    ],
    []
  );

  const isReprocessed = (row: RawDriveInfo) => {
    const recordingId = `${row.organization_id}-${row.id}`;
    return reprocessedIds.has(recordingId);
  };

  const tableOptions: MRT_TableOptions<RawDriveInfo> = {
    columns,
    data: data || [],
    initialState: {
      pagination: { pageSize: 20, pageIndex: 0 },
      columnPinning: {
        right: ['mrt-row-actions'],
        left: ['mrt-row-numbers', 'organization_name'],
      },
      showGlobalFilter: true,
    },
    enableRowNumbers: true,
    manualPagination: true,
    onPaginationChange: setPagination,
    enableSorting: false,
    enableColumnPinning: false,
    enableTopToolbar: false,
    rowCount: meta?.totalCount ?? 0,
    enableRowActions: true,
    positionActionsColumn: 'last',
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: {
        maxHeight: `calc(100vh - 425px)`,
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor:
          row.index % 2 === 0 ? 'rgb(48, 48, 48)' : 'rgb(35, 35, 35)',
      },
      style: { tableLayout: 'auto' },
    }),
    muiPaginationProps: {
      rowsPerPageOptions: [25, 50, 100, 200],
    },
    getRowId: (originalRow) =>
      `${originalRow.organization_id} - ${originalRow.id}`,
    renderRowActions: ({ row }) => {
      const isReproc = isReprocessed(row.original);

      return (
        <Box flexWrap="nowrap" display="flex" alignItems="center" gap={2}>
          <RowActionButton<RawDriveInfo>
            title={isReproc ? 'Already reprocessed' : 'Reprocess recording'}
            icon={
              isReproc ? (
                <Icon
                  icon="refresh"
                  color="green.300"
                  sx={{
                    animation: 'spin 2s linear infinite',
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(0deg)',
                      },
                      '100%': {
                        transform: 'rotate(360deg)',
                      },
                    },
                  }}
                />
              ) : (
                <Icon icon="refresh" />
              )
            }
            row={row.original}
            onClickHandler={() => {
              if (!isReproc) handleReprocessRecording(row.original);
            }}
            isDisabled={isReproc}
          />
        </Box>
      );
    },
    state: {
      isLoading: isLoading || isRefetching,
      pagination,
      showLoadingOverlay: false,
      showProgressBars: isLoading || isRefetching,
      showSkeletons: isLoading || isRefetching,
    },
    ...createPaginationConfig(meta, pagination),
  };

  return (
    <DashboardCard
      icon={<CgDatabase size={18} />}
      title={`${osfStatus === 'NOT_STARTED' ? 'Stale' : 'Failed'} Recordings (${
        meta?.totalCount ?? 0
      } total)`}
      headerExtra={
        <Button
          variant="outline"
          onClick={handleRefresh}
          data-testid="recording-mgmt-refresh-btn"
        >
          Refresh list
        </Button>
      }
      style={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        height: '100%',
        overflow: 'hidden',
      }}
      data-testid="recording-mgmt-stale-card"
    >
      <Box
        overflow="hidden"
        height="100%"
        border="1px solid"
        borderColor="whiteAlpha.200"
      >
        <ThemedMaterialTableWrapper {...tableOptions} />
      </Box>
    </DashboardCard>
  );
}
